import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import apiService from '../services/api';

// Note: preferences API goes through userMgmtClient (user-management service)

const PreferencesContext = createContext();

const LS_WATCHLIST_KEY = 'workbench_watchlist';
const LS_WATCHLIST_VER_KEY = 'workbench_watchlist_v';
const WATCHLIST_VERSION = 2;
const LS_PORTFOLIO_KEY = 'workbench_portfolio_stocks';
const LS_PORTFOLIOS_KEY = 'workbench_portfolios';

const DEFAULT_WATCHLIST = [
    // Magnificent 7
    'AAPL', 'MSFT', 'NVDA', 'GOOGL', 'TSLA', 'AMZN', 'META',
    // Semiconductors
    'AMD', 'AVGO', 'QCOM', 'TSM', 'ARM', 'ASML', 'INTC',
    // Application / Software giants
    'NFLX', 'CRM', 'ADBE', 'NOW', 'PLTR', 'UBER',
];

// ---- localStorage helpers ----

const loadWatchlistFromLS = () => {
    try {
        const ver = localStorage.getItem(LS_WATCHLIST_VER_KEY);
        if (Number(ver) < WATCHLIST_VERSION) {
            localStorage.setItem(LS_WATCHLIST_VER_KEY, String(WATCHLIST_VERSION));
            localStorage.removeItem(LS_WATCHLIST_KEY);
            return DEFAULT_WATCHLIST;
        }
        const saved = localStorage.getItem(LS_WATCHLIST_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            if (Array.isArray(parsed) && parsed.length > 0) return parsed;
        }
    } catch (err) {
        console.warn('Failed to load watchlist from localStorage:', err);
    }
    return DEFAULT_WATCHLIST;
};

const saveWatchlistToLS = (list) => {
    try {
        localStorage.setItem(LS_WATCHLIST_KEY, JSON.stringify(list));
    } catch (err) {
        console.warn('Failed to save watchlist to localStorage:', err);
    }
};

const loadPortfolioFromLS = () => {
    try {
        const saved = localStorage.getItem(LS_PORTFOLIO_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            if (Array.isArray(parsed)) return parsed;
        }
    } catch (err) {
        console.warn('Failed to load portfolio stocks:', err);
    }
    return [];
};

const savePortfolioToLS = (list) => {
    try {
        localStorage.setItem(LS_PORTFOLIO_KEY, JSON.stringify(list));
    } catch (err) {
        console.warn('Failed to save portfolio stocks:', err);
    }
};

const loadPortfoliosFromLS = () => {
    try {
        const saved = localStorage.getItem(LS_PORTFOLIOS_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            if (Array.isArray(parsed)) return parsed;
        }
    } catch (err) {
        console.warn('Failed to load portfolios:', err);
    }
    return [];
};

const savePortfoliosToLS = (list) => {
    try {
        localStorage.setItem(LS_PORTFOLIOS_KEY, JSON.stringify(list));
    } catch (err) {
        console.warn('Failed to save portfolios:', err);
    }
};

// ---- Provider ----

export const PreferencesProvider = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const [preferences, setPreferences] = useState(() => ({
        watchlist: loadWatchlistFromLS(),
        portfolio_stocks: loadPortfolioFromLS(),
        portfolios: loadPortfoliosFromLS(),
    }));
    const [isLoading, setIsLoading] = useState(false);
    const debounceTimer = useRef(null);
    const prevAuth = useRef(isAuthenticated);

    // Sync from server when user logs in
    useEffect(() => {
        // Only fetch when transitioning from not-authenticated to authenticated
        if (isAuthenticated && !prevAuth.current) {
            setIsLoading(true);
            apiService.getUserPreferences()
                .then((serverPrefs) => {
                    const merged = { ...preferences };
                    if (Array.isArray(serverPrefs.watchlist) && serverPrefs.watchlist.length > 0) {
                        merged.watchlist = serverPrefs.watchlist;
                        saveWatchlistToLS(merged.watchlist);
                    }
                    if (Array.isArray(serverPrefs.portfolio_stocks)) {
                        merged.portfolio_stocks = serverPrefs.portfolio_stocks;
                        savePortfolioToLS(merged.portfolio_stocks);
                    }
                    if (Array.isArray(serverPrefs.portfolios)) {
                        merged.portfolios = serverPrefs.portfolios;
                        savePortfoliosToLS(merged.portfolios);
                    }
                    setPreferences(merged);
                })
                .catch(() => {
                    // Server unavailable — keep localStorage values
                })
                .finally(() => setIsLoading(false));
        }
        prevAuth.current = isAuthenticated;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    // Also load on mount if already authenticated (page refresh)
    useEffect(() => {
        if (!isAuthenticated) return;
        setIsLoading(true);
        apiService.getUserPreferences()
            .then((serverPrefs) => {
                const merged = {};
                merged.watchlist = (Array.isArray(serverPrefs.watchlist) && serverPrefs.watchlist.length > 0)
                    ? serverPrefs.watchlist : loadWatchlistFromLS();
                merged.portfolio_stocks = Array.isArray(serverPrefs.portfolio_stocks)
                    ? serverPrefs.portfolio_stocks : loadPortfolioFromLS();
                merged.portfolios = Array.isArray(serverPrefs.portfolios)
                    ? serverPrefs.portfolios : loadPortfoliosFromLS();
                saveWatchlistToLS(merged.watchlist);
                savePortfolioToLS(merged.portfolio_stocks);
                savePortfoliosToLS(merged.portfolios);
                setPreferences(merged);
            })
            .catch(() => {
                // Server unavailable — keep localStorage values
            })
            .finally(() => setIsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Debounced save to server
    const syncToServer = useCallback((newPrefs) => {
        if (!isAuthenticated) return;
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            apiService.saveUserPreferences(newPrefs).catch(() => {
                // Silent fail — localStorage already has the data
            });
        }, 500);
    }, [isAuthenticated]);

    // Generic preference updater
    const updatePreference = useCallback((key, value) => {
        setPreferences(prev => {
            const next = { ...prev, [key]: value };
            // Persist to localStorage
            if (key === 'watchlist') saveWatchlistToLS(value);
            if (key === 'portfolio_stocks') savePortfolioToLS(value);
            if (key === 'portfolios') savePortfoliosToLS(value);
            // Debounced sync to server
            syncToServer({ [key]: value });
            return next;
        });
    }, [syncToServer]);

    // ---- Watchlist convenience methods ----

    const watchlist = preferences.watchlist;

    const setWatchlist = useCallback((list) => {
        updatePreference('watchlist', list);
    }, [updatePreference]);

    const addToWatchlist = useCallback((symbol) => {
        const s = symbol.trim().toUpperCase();
        if (!s) return;
        setPreferences(prev => {
            if (prev.watchlist.includes(s)) return prev;
            const next = [...prev.watchlist, s];
            saveWatchlistToLS(next);
            syncToServer({ watchlist: next });
            return { ...prev, watchlist: next };
        });
    }, [syncToServer]);

    const removeFromWatchlist = useCallback((symbol) => {
        setPreferences(prev => {
            const next = prev.watchlist.filter(x => x !== symbol);
            saveWatchlistToLS(next);
            syncToServer({ watchlist: next });
            return { ...prev, watchlist: next };
        });
    }, [syncToServer]);

    // ---- Portfolio convenience methods ----

    const portfolioStocks = preferences.portfolio_stocks;

    const setPortfolioStocks = useCallback((list) => {
        updatePreference('portfolio_stocks', list);
    }, [updatePreference]);

    // ---- Saved portfolios convenience methods ----

    const portfolios = preferences.portfolios;

    const savePortfolio = useCallback((name, stocks) => {
        setPreferences(prev => {
            const existing = prev.portfolios.filter(p => p.name !== name);
            const next = [...existing, { name, stocks: [...stocks] }];
            savePortfoliosToLS(next);
            syncToServer({ portfolios: next });
            return { ...prev, portfolios: next };
        });
    }, [syncToServer]);

    const deletePortfolio = useCallback((name) => {
        setPreferences(prev => {
            const next = prev.portfolios.filter(p => p.name !== name);
            savePortfoliosToLS(next);
            syncToServer({ portfolios: next });
            return { ...prev, portfolios: next };
        });
    }, [syncToServer]);

    const loadPortfolio = useCallback((name) => {
        const found = preferences.portfolios.find(p => p.name === name);
        if (found) setPortfolioStocks([...found.stocks]);
    }, [preferences.portfolios, setPortfolioStocks]);

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
        };
    }, []);

    return (
        <PreferencesContext.Provider value={{
            preferences,
            updatePreference,
            watchlist,
            setWatchlist,
            addToWatchlist,
            removeFromWatchlist,
            portfolioStocks,
            setPortfolioStocks,
            portfolios,
            savePortfolio,
            deletePortfolio,
            loadPortfolio,
            isLoading,
        }}>
            {children}
        </PreferencesContext.Provider>
    );
};

export const usePreferences = () => {
    const ctx = useContext(PreferencesContext);
    if (!ctx) throw new Error('usePreferences must be used within PreferencesProvider');
    return ctx;
};

export default PreferencesContext;
